import {useEffect, useRef, useState} from "react"
import {Badge, Menu, MenuItem, ToggleButton} from "@mui/material"
import {DriveFileRenameOutline} from "@mui/icons-material"

const HighlightColors = [
	{name: "yellow", hex: "#FFFF77"}, // DEFAULT - yellow
	{name: "orange", hex: "#FFAE77"}, // orange,
	{name: "red", hex: "#FF7792"}, // red,
	{name: "purple", hex: "#C977FF"}, // purple
	{name: "blue", hex: "#77C9FF"}, // blue
	{name: "green", hex: "#AEFF77"}, // green
]

export function HighlightMenu({editor, editorState}) {
	const [anchorEl, setAnchorEl] = useState(null)
	const [currentColor, setCurrentColor] = useState(HighlightColors[0].hex)
	const menuOpen = Boolean(anchorEl)
	const highlightColorMenuRef = useRef(null)
	const lastColorRef = useRef(HighlightColors[0].hex)
	const toggleHighlightColor = () => {
		editor.chain().focus().toggleHighlight({color: currentColor}).run()
	}
	const setHighlightColor = (color) => {
		setCurrentColor(color)
		lastColorRef.current = color
		editor.chain().focus().toggleHighlight({color}).run()
		setAnchorEl(null)
	}

	/**
	 * UE listening for changes on the editorState's currentHighlightColor
	 * When a change is detected, we show the user the color that is set for that node. If no color is set,
	 * 		we fall back on the last color the user set by looking at the lastColorRef.
	 */
	useEffect(() => {
		if(editorState.currentHighlightColor !== undefined) { // undefined means the current node doesn't have a highlight color set
			const selectedColor = HighlightColors.find(({hex}) => hex === editorState.currentHighlightColor)
			if(selectedColor) {
				// preserve last user selected
				lastColorRef.current = currentColor
				setCurrentColor(selectedColor.hex)
			} else {
				setCurrentColor(lastColorRef.current)
			}
		} else {
			setCurrentColor(lastColorRef.current)
		}
	}, [editorState.currentHighlightColor])

	return (
		<div>
			<Badge
				badgeContent={""}
				overlap={"circular"}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				sx={{
					"& .MuiBadge-badge": {
						backgroundColor: currentColor,
					}
				}}
				slotProps={{
					badge: {
						onClick: (e) => setAnchorEl(e.currentTarget),
						ref: highlightColorMenuRef,
						sx: {
							cursor: "pointer",
						}
					}
				}}
			>
				<ToggleButton
					value={"na"}
					selected={editorState.isHighlightColor}
					className={editorState.isHighlightColor ? "Mui-selected" : ""}
					onClick={toggleHighlightColor}
				>
					<DriveFileRenameOutline />
				</ToggleButton>
			</Badge>
			<Menu
				open={menuOpen}
				onClose={() => setAnchorEl(null)}
				anchorEl={highlightColorMenuRef.current}
				variant={"selectedMenu"}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				sx={{
					"& .MuiMenu-list": {
						padding: 0
					}
				}}
			>
				{HighlightColors.map((color, index) => (
					<MenuItem
						dense
						key={index}
						onClick={() => setHighlightColor(color.hex)}
						selected={color.hex === currentColor}
						sx={{
							"&.Mui-selected": {
								backgroundColor: color.hex + "BE",
								border: "2px solid black"
							},
							"&.Mui-selected:hover": {
								backgroundColor: color.hex + "BE",
							},
							"&.Mui-selected.Mui-focusVisible": {
								backgroundColor: color.hex + "BE",
							},
							backgroundColor: color.hex,
						}}
					>
						{color.name}
					</MenuItem>
				))}
			</Menu>
		</div>
	)
}